import { getStoreOpeningDateValues } from "@faire/web--source/common/consts/STORE_OPENING_DATE_VALUES";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { intlDate } from "@faire/web--source/common/localization/date-fns/intlDate";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { addDays } from "date-fns/addDays";
import { addMonths } from "date-fns/addMonths";
import { startOfMonth } from "date-fns/startOfMonth";
import { subYears } from "date-fns/subYears";

export const getPreviousYearsMonthDateOptions = (): OpeningMonthOption[] => {
  const startOfLastYear = subYears(new Date(new Date().getFullYear(), 0, 1), 1);
  const monthsOptions = getMonthDropdownOptions(startOfLastYear, [
    ...Array(12).keys(),
  ]);
  return monthsOptions.map((dateTimestamp) => ({
    monthLabel: intlDate(dateTimestamp, "MMMM"),
    yearLabel: startOfLastYear.getFullYear().toString(),
    value: dateTimestamp,
  }));
};

export const getThisYearsMonthDateOptions = (
  strictLocalize: StrictLocalizeFunction
): OpeningMonthOption[] => {
  const startOfThisYear = new Date(new Date().getFullYear(), 0, 1);
  const monthsOptions = getMonthDropdownOptions(startOfThisYear, [
    ...Array(new Date().getUTCMonth() + 1).keys(),
  ]);
  return monthsOptions.reverse().map((dateTimestamp, index) => {
    return {
      monthLabel:
        index === 0
          ? strictLocalize({
              defaultMessage: "This month",
              description: {
                text: "Option in a dropdown menu for selecting a store opening date for this month.",
              },
            })
          : intlDate(dateTimestamp, "MMMM"),
      yearLabel:
        index === 0
          ? undefined
          : new Date(dateTimestamp).getFullYear().toString(),
      value: dateTimestamp,
    };
  });
};

export const getOpeningSoonOptions = (
  strictLocalize: StrictLocalizeFunction
): OpeningMonthOption[] => {
  const monthValues = getStoreOpeningDateValues();

  const dateOptions: (OpeningMonthOption | undefined)[] = [
    monthValues[0]
      ? {
          monthLabel: strictLocalize({
            defaultMessage: "This month",
            description: {
              text: "Option in a dropdown menu for selecting a store opening date for this month.",
            },
          }),
          yearLabel: undefined,
          value: monthValues[0],
        }
      : undefined,
    monthValues[1]
      ? {
          monthLabel: strictLocalize({
            defaultMessage: "Next month",
            description: {
              text: "Option in a dropdown menu for selecting a store opening date for next month.",
            },
          }),
          yearLabel: undefined,
          value: monthValues[1],
        }
      : undefined,
    ...monthValues.slice(2, 6).map((monthValue) =>
      monthValue
        ? {
            monthLabel: capitalizeFirstLetter(intlDate(monthValue, "MMMM")),
            yearLabel: new Date(monthValue).getFullYear().toString(),
            value: monthValue,
          }
        : undefined
    ),
    monthValues[5] && monthValues[6]
      ? {
          monthLabel: strictLocalize(
            {
              defaultMessage: "After {formattedDate}",
              description: {
                text: "Option in a dropdown menu for selecting a store opening date after a certain month.",
              },
            },
            { formattedDate: intlDate(monthValues[5], "MMMM") }
          ),
          yearLabel: new Date(monthValues[5]).getFullYear().toString(),
          value: monthValues[6],
        }
      : undefined,
  ];

  return dateOptions.filter(isNotUndefined);
};

type OpeningMonthOption = {
  monthLabel: string;
  yearLabel?: string;
  value: number;
};

const getMonthDropdownOptions = (startingDate: Date, months: Array<number>) => {
  const startingDateAdjusted =
    startingDate.getMonth() !== startingDate.getUTCMonth()
      ? addDays(startOfMonth(addDays(startingDate, 1)), 1)
      : addDays(startOfMonth(startingDate), 1);

  const monthsOptions = months.map((addAmount) => {
    return addMonths(startingDateAdjusted, addAmount).getTime();
  });

  return monthsOptions;
};

// some intl dates are not capitalized and we cannot use CSS to capitalize them because they capitalize the first letter of every word
const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
};
